import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import {
  readConsent,
  grantConsent,
  denyConsent,
  CONSENT_OPEN_EVENT,
  CONSENT_GRANTED,
} from '@/lib/analytics';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);

  // Erstaufruf: Banner nur zeigen, wenn noch keine gültige Entscheidung vorliegt.
  useEffect(() => {
    const stored = readConsent();
    setCurrent(stored);
    if (stored) return;
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Widerruf / Änderung über den Footer-Link.
  useEffect(() => {
    const open = () => {
      setCurrent(readConsent());
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
  }, []);

  const choose = useCallback((granted) => {
    if (granted) grantConsent();
    else denyConsent();
    setCurrent(granted ? 'granted' : 'denied');
    setVisible(false);
  }, []);

  // Escape schließt das Banner nur, wenn bereits eine Entscheidung existiert —
  // sonst gäbe es einen Weg, das Banner ohne Entscheidung loszuwerden.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && current) setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, current]);

  const buttonBase =
    'flex-1 px-4 py-2 text-xs uppercase tracking-[0.12em] font-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ink';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="fixed bottom-6 left-6 right-6 md:right-auto z-[9999] md:max-w-sm"
          role="dialog"
          aria-modal="false"
          aria-label={t('cookie.title')}
          data-testid="cookie-consent"
        >
          <div className="bg-ink/95 backdrop-blur-xl border border-smoke/40 p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <Cookie size={18} className="text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-body font-semibold mb-2">
                  {t('cookie.title')}
                </p>
                <p className="text-sm text-cream-muted font-body leading-relaxed">
                  {t('cookie.text')}{' '}
                  <Link
                    to="/legal/privacy"
                    className="text-gold hover:text-gold-soft transition-colors underline underline-offset-2"
                  >
                    {t('cookie.privacy')}
                  </Link>
                  .
                </p>

                {/* Beide Buttons bewusst gleich groß und gleich prominent —
                    ein zurückhaltender "Ablehnen"-Link wäre nicht rechtskonform. */}
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => choose(true)}
                    data-testid="cookie-accept"
                    className={`${buttonBase} bg-gold text-obsidian hover:bg-gold-soft`}
                  >
                    {t('cookie.accept')}
                  </button>
                  <button
                    type="button"
                    onClick={() => choose(false)}
                    data-testid="cookie-reject"
                    className={`${buttonBase} bg-smoke text-cream hover:bg-smoke/70`}
                  >
                    {t('cookie.reject')}
                  </button>
                </div>

                {current && (
                  <p className="text-[11px] text-cream-muted/70 font-body mt-3">
                    {current === CONSENT_GRANTED
                      ? t('cookie.currentAccepted')
                      : t('cookie.currentRejected')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
